import { CircularProgressIcon } from '@/components/common/circular-progress-icon';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { useEventStore } from '@/state/stores/use-event-store';
import type { EventDto, EventDayDto } from '@/types/dtos/events/response/event.dto';
import { Person, PeopleOutline, VideoLibrary } from '@mui/icons-material';
import { Stack, Typography, Select, MenuItem, FormControl, Box, Button } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AddFlowModal from '../flows/tab-actions/add-flow.modal';
import { useMemo, useState } from 'react';
import Widget from '@/components/common/widget.tsx';
import Tooltip from '@/components/common/tooltip';
import { useNavigate } from 'react-router';
import { getFlowDayAndTimeInfo, getFlowModerators } from '@/components/events/helpers/get-flow-day-number.helper';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { getObjectId } from '@/helpers/object.id.helper';
import { masterFlowsForEventList } from '@/helpers/flow-scope.helper';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { EventSlotDto } from '@/types/dtos/events/response/event.dto';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';
import {
  getFlowContributors,
  getContributorFlowRoles,
  getFlowModeratorInviteAggregates,
} from '@/utils/contributor-extraction.utils';

// Helper function to get contributor data for a flow
const getContributorData = (flow: FlowDto, allContributors: ContributorDto[]) => {
  if (!allContributors) return { totalInvited: 0, totalConfirmed: 0 };

  let totalInvited = 0;
  let totalConfirmed = 0;

  // 1. Count direct flow contributors using the new utility function
  const directFlowContributors = getFlowContributors(flow, allContributors);
  const directFlowContributorIds = new Set(directFlowContributors.map((contributor) => contributor.id));

  directFlowContributors.forEach((contributor) => {
    const flowId = typeof flow.origin === 'string' ? flow.origin : flow.id;
    const flowRoles = getContributorFlowRoles(contributor, flowId);

    for (const role of flowRoles) {
      if (role.invitedAt) {
        totalInvited++;
      }
      if (role.acceptedAt) {
        totalConfirmed++;
      }
    }
  });

  // 2. Count contributors with slot assignments in this flow
  const flowSlotIds = new Set(flow.slots?.map((slot) => slot.id) ?? []);

  allContributors?.forEach((contributor) => {
    if (directFlowContributorIds.has(contributor.id)) return; // Already counted above

    const slotRoles = contributor.roles.filter((role) =>
      role.slots?.some((slot) => {
        if (slot.entityType !== 'FlowSlot') return false;
        const entityId = typeof slot.entity === 'string' ? slot.entity : slot.entity.id;
        return flowSlotIds.has(entityId);
      }),
    );

    for (const role of slotRoles) {
      if (role.invitedAt) {
        totalInvited++;
      }
      if (role.acceptedAt) {
        totalConfirmed++;
      }
    }
  });

  return { totalInvited, totalConfirmed };
};

// Helper function to get the latest asset version for a media item
const getLatestAsset = (media: { assets?: Array<{ version: number; isApproved?: boolean }> }) => {
  if (!media.assets || media.assets.length === 0) {
    return null;
  }
  // Sort by version descending and get the first (latest) asset
  return [...media.assets].sort((a, b) => b.version - a.version)[0];
};

// Helper function to get media data for a flow
const getMediaData = (flow: FlowDto) => {
  // Collect all related media objects:
  // 1. Flow media without placement (flow-level media)
  // 2. Flow media with placement matching any slot ID (slot-specific flow media)
  // 3. Session media from slot content (session media)
  const relatedMedia: Array<{ assets?: Array<{ version: number; isApproved?: boolean }> }> = [];

  // 1. Flow media without placement
  if (flow.media) {
    const flowMediaWithoutPlacement = flow.media.filter((media) => !media.placement);
    relatedMedia.push(...flowMediaWithoutPlacement);
  }

  // 2. Flow media with placement matching any slot ID
  if (flow.media && flow.slots) {
    const slotIds = new Set(flow.slots.map((slot) => slot.id));
    const flowMediaWithPlacement = flow.media.filter(
      (media) => media.placement?.type === 'Slot' && slotIds.has(media.placement.id),
    );
    relatedMedia.push(...flowMediaWithPlacement);
  }

  // 3. Session media from slot content
  flow.slots?.forEach((slot) => {
    if (slot.content && typeof slot.content === 'object' && 'media' in slot.content) {
      const contentMedia = slot.content.media;
      if (Array.isArray(contentMedia)) {
        relatedMedia.push(...contentMedia);
      }
    }
  });

  // Count total related media objects
  const totalRelatedMedia = relatedMedia.length;

  // Count how many have latest asset version approved
  const approvedCount = relatedMedia.filter((media) => {
    const latestAsset = getLatestAsset(media);
    return latestAsset !== null && latestAsset.isApproved === true;
  }).length;

  return { totalRelatedMedia, approvedCount };
};

// Helper function to get the status for contributors
const getContributorStatus = (totalInvited: number, totalConfirmed: number) => {
  if (totalInvited === 0) {
    // No invitations issued: circle → full, inactive, icon → inactive
    return {
      circleColor: 'action.disabled',
      iconColor: 'disabled',
      active: false,
      progress: 100,
    };
  }

  if (totalConfirmed === 0) {
    // Invitations issued but no confirmations yet: circle → full inactive, icon → active
    return {
      circleColor: 'action.disabled',
      iconColor: 'action',
      active: false,
      progress: 100,
    };
  }

  if (totalConfirmed < totalInvited) {
    // Confirmations pending: circle → info, icon → active
    const progress = Math.round((totalConfirmed / totalInvited) * 100);
    return {
      circleColor: 'info.main',
      iconColor: 'action',
      active: true,
      progress,
    };
  }

  // All invitations confirmed: circle → success, icon → active
  return {
    circleColor: 'success.main',
    iconColor: 'action',
    active: true,
    progress: 100,
  };
};

/**
 * Maps moderator role-row aggregates to ring + icon colors. `confirmed` is fully accepted units
 * (role + event-slot context); progress is share of all moderator roles complete.
 */
const getModeratorIndicatorStatus = (aggregates: { total: number; invited: number; confirmed: number }) => {
  const { total, invited, confirmed } = aggregates;
  if (total === 0) {
    return {
      circleColor: 'action.disabled',
      iconColor: 'disabled',
      active: false,
      progress: 100,
    };
  }
  if (invited === 0) {
    return {
      circleColor: 'action.disabled',
      iconColor: 'action',
      active: false,
      progress: 100,
    };
  }
  if (confirmed < total) {
    return {
      circleColor: 'info.main',
      iconColor: 'action',
      active: true,
      progress: Math.round((confirmed / total) * 100),
    };
  }
  return {
    circleColor: 'success.main',
    iconColor: 'action',
    active: true,
    progress: 100,
  };
};

// Helper function to get the status for media
const getMediaStatus = (totalRelatedMedia: number, approvedCount: number) => {
  if (totalRelatedMedia === 0) {
    // No related media objects: circle → full, inactive, icon → inactive
    return {
      circleColor: 'action.disabled',
      iconColor: 'disabled',
      active: false,
      progress: 100,
    };
  }

  // Calculate progress: (# approved) / (# total) * 100
  const progress = Math.round((approvedCount / totalRelatedMedia) * 100);

  if (approvedCount === totalRelatedMedia) {
    // All media objects have approved latest assets: circle → success, icon → active
    return {
      circleColor: 'success.main',
      iconColor: 'action',
      active: true,
      progress: 100,
    };
  }

  if (approvedCount === 0) {
    // No approved assets: circle → full inactive, icon → active
    return {
      circleColor: 'action.disabled',
      iconColor: 'action',
      active: true,
      progress: 0,
    };
  }

  // Some approved, some not: circle → info, icon → active
  return {
    circleColor: 'info.main',
    iconColor: 'action',
    active: true,
    progress,
  };
};

// DaySelect component extracted to avoid creating components during render
function DaySelect({
  event,
  selectedDay,
  handleDayChange,
  t,
}: {
  event: EventDto | undefined;
  selectedDay: string;
  handleDayChange: (dayId: string) => void;
  t: ReturnType<typeof useTranslations>;
}) {
  if (!event?.days || event.days.length <= 1) {
    return null;
  }

  return (
    <Box>
      <FormControl size="small" variant="standard" fullWidth>
        <Select
          value={selectedDay}
          onChange={(e) => handleDayChange(e.target.value)}
          displayEmpty
          data-testid="flow-day-filter-select"
        >
          <MenuItem value="all">{t('event.detail.dashboard.flows.allDays')}</MenuItem>
          {event?.days?.map((day: EventDayDto, i: number) => (
            <MenuItem key={`day-${i}`} value={day.id}>
              {event?.startDate && t('event.detail.dashboard.flows.day', { dayNumber: (i + 1).toString() })}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default function Flows() {
  const t = useTranslations();
  const event = useEventStore((s) => s.event);
  const eventFlows = useEventStore((s) => s.eventFlows);
  const contributors = useEventStore((s) => s.contributors);
  const [addFlowsOpen, setAddFlowsOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string>('all');
  const navigate = useNavigate();
  const mode = useEventMode();

  // Filter to master PLAN flows only (scoped instance documents are not separate list entries)
  const planFlows = useMemo(
    () => masterFlowsForEventList(eventFlows).filter((flow) => flow.mode === 'PLAN'),
    [eventFlows],
  );

  // Create items for each eventSlot instead of each flow
  // Each eventSlot gets its own item in the dashboard
  const flowEventSlotItems = useMemo(() => {
    if (!planFlows || !event?.slots || !event?.days) return [];

    const items: Array<{ flow: FlowDto; eventSlot: EventSlotDto }> = [];

    planFlows.forEach((flow) => {
      // Find all eventSlots for this flow
      const flowEventSlots = event.slots.filter(
        (slot: EventSlotDto) =>
          slot.contentType === EVENT_SLOT_TYPE.FLOW &&
          (slot.content === flow.id ||
            slot.content === flow.origin ||
            (typeof flow.origin === 'object' && flow.origin?.id && slot.content === flow.origin.id)),
      );

      // Create an item for each eventSlot
      flowEventSlots.forEach((eventSlot) => {
        items.push({ flow, eventSlot });
      });
    });

    // Filter by selected day if not 'all'
    const filteredItems = selectedDay === 'all' ? items : items.filter((item) => item.eventSlot.day.id === selectedDay);

    // Sort by day (using day index in event.days) and then by startOffsetMinutes
    return filteredItems.sort((a, b) => {
      const dayAIndex = event.days.findIndex((day) => day.id === a.eventSlot.day.id);
      const dayBIndex = event.days.findIndex((day) => day.id === b.eventSlot.day.id);

      // First sort by day
      if (dayAIndex !== dayBIndex) {
        return dayAIndex - dayBIndex;
      }

      // Then sort by startOffsetMinutes within the same day
      const startOffsetA = a.eventSlot.startOffsetMinutes ?? 0;
      const startOffsetB = b.eventSlot.startOffsetMinutes ?? 0;
      return startOffsetA - startOffsetB;
    });
  }, [planFlows, selectedDay, event]);

  const handleDayChange = (dayId: string) => {
    setSelectedDay(dayId);
  };

  const flowCount = flowEventSlotItems?.length ?? 0;
  const flowLabelKey =
    flowCount === 1
      ? ('event.detail.dashboard.flows.scheduledFlow' as TranslationKey)
      : ('event.detail.dashboard.flows.scheduledFlows' as TranslationKey);
  const flowLabelFallback = flowCount === 1 ? 'Scheduled Flow' : 'Scheduled Flows';

  return (
    <Widget
      data-testid="event-flows"
      title={`${flowCount} ${t(flowLabelKey, undefined, flowLabelFallback)}`}
      leftControls={<DaySelect event={event} selectedDay={selectedDay} handleDayChange={handleDayChange} t={t} />}
      primaryButton={{
        label: t('common.new'),
        onClick: () => setAddFlowsOpen(true),
        disabled: isOrganizeMode(mode),
        testId: 'event-flows-add-flow-button',
        startIcon: <AddOutlinedIcon />,
      }}
    >
      <Stack spacing="8px">
        {flowEventSlotItems?.map((item, index) => {
          const { flow, eventSlot } = item;
          const flowId = flow?.origin ? getObjectId(flow.origin) : flow.id;
          return (
            <Stack
              key={`flow-${flow.id}-slot-${eventSlot.id}-${index}`}
              data-testid={`flow-event-slot-${index}`}
              direction="row"
              gap={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                'p': 1,
                'border': 1,
                'borderColor': 'divider',
                'borderRadius': 2,
                'boxShadow': 1,
                'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                'backgroundColor': (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                  boxShadow: 2,
                },
              }}
            >
              <Stack key={flow.id} spacing={0}>
                <Tooltip title={t('flows.clickToGoToDetailTooltip')}>
                  <Typography
                    variant="body2"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      const eventId = event?.origin ? getObjectId(event.origin) : event?.id;
                      if (eventId) {
                        navigate(`/flow/${flowId}?eventSlotId=${eventSlot.id}&contextType=event&contextId=${eventId}`);
                      } else {
                        navigate(`/flow/${flowId}?eventSlotId=${eventSlot.id}`);
                      }
                    }}
                  >
                    {flow.title}
                  </Typography>
                </Tooltip>
                <Typography variant="subtitle3" color="text.secondary">
                  {(() => {
                    const dayAndTimeInfo = getFlowDayAndTimeInfo(flow, event, eventSlot);
                    const moderators = getFlowModerators(flow, contributors || [], event?.multiFlow);
                    const parts = [dayAndTimeInfo, moderators].filter(Boolean);
                    return parts.join(' | ');
                  })()}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {(() => {
                    const contributorData = getContributorData(flow, contributors || []);
                    const contributorStatus = getContributorStatus(
                      contributorData.totalInvited,
                      contributorData.totalConfirmed,
                    );

                    return (
                      <CircularProgressIcon
                        icon={<Person sx={{ width: 16, height: 16, color: contributorStatus.iconColor }} />}
                        progress={contributorStatus.progress}
                        color={contributorStatus.circleColor}
                        active={contributorStatus.active}
                        size={28}
                      />
                    );
                  })()}
                  {(() => {
                    const moderatorAggregates = getFlowModeratorInviteAggregates(
                      flow,
                      contributors || [],
                      event?.multiFlow,
                      eventSlot.id,
                    );
                    const moderatorStatus = getModeratorIndicatorStatus(moderatorAggregates);

                    return (
                      <CircularProgressIcon
                        icon={<PeopleOutline sx={{ width: 16, height: 16, color: moderatorStatus.iconColor }} />}
                        progress={moderatorStatus.progress}
                        color={moderatorStatus.circleColor}
                        active={moderatorStatus.active}
                        size={28}
                      />
                    );
                  })()}
                  {(() => {
                    const mediaData = getMediaData(flow);
                    const mediaStatus = getMediaStatus(mediaData.totalRelatedMedia, mediaData.approvedCount);

                    return (
                      <CircularProgressIcon
                        icon={<VideoLibrary sx={{ width: 16, height: 16, color: mediaStatus.iconColor }} />}
                        progress={mediaStatus.progress}
                        color={mediaStatus.circleColor}
                        active={mediaStatus.active}
                        size={28}
                      />
                    );
                  })()}
                </Stack>
                <Button
                  color="info"
                  size="small"
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    const eventId = event?.origin ? getObjectId(event.origin) : event?.id;
                    if (eventId) {
                      navigate(`/flow/${flowId}?eventSlotId=${eventSlot.id}&contextType=event&contextId=${eventId}`);
                    } else {
                      navigate(`/flow/${flowId}?eventSlotId=${eventSlot.id}`);
                    }
                  }}
                  sx={{
                    minWidth: 'unset',
                    padding: '2px 8px',
                    marginLeft: 2,
                  }}
                >
                  {t('common.studio')}
                </Button>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
      <AddFlowModal open={addFlowsOpen} setOpen={setAddFlowsOpen} />
    </Widget>
  );
}
